// Growth planners: G05 pools mapper, G06 bets matrix, G07 one-page plan,
// G08 90-day sprint, G09 driver tree, G10 founder transition.
import { useMemo, useState } from "react";
import { prioritizeBets } from "./engines";
import { Field, Note, ToolSection, useNumbers } from "./ui";
import { inr } from "./engines";

/* ---------- G06 Growth Bets Prioritization Matrix ---------- */
const CRITERIA = [["impact", "Impact"], ["speed", "Speed"], ["fit", "Strategic fit"], ["investment", "Affordability (5 = cheap)"], ["risk", "Low risk (5 = safe)"], ["confidence", "Confidence"]];

export function ToolG06() {
  const [bets, setBets] = useState([
    { name: "Enter South India", impact: 5, speed: 2, fit: 4, investment: 2, risk: 3, confidence: 3 },
    { name: "Launch premium SKU", impact: 3, speed: 4, fit: 4, investment: 4, risk: 4, confidence: 4 },
    { name: "Marketplace push", impact: 4, speed: 4, fit: 3, investment: 3, risk: 3, confidence: 4 },
  ]);
  const [w, setW] = useNumbers({ impact: 3, speed: 2, fit: 3, investment: 2, risk: 1, confidence: 1 });
  const r = useMemo(() => prioritizeBets({ bets: bets.filter((b) => b.name), weights: w }), [bets, w]);
  const setB = (i, k) => (e) => setBets((bs) => bs.map((b, j) => (j === i ? { ...b, [k]: k === "name" ? e.target.value : Number(e.target.value) } : b)));

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <p className="text-sm text-zinc-600">Score each bet 1–5 per criterion. Weights are yours to set — watch the ranking change as they do.</p>
        {bets.map((b, i) => (
          <div key={i} className="border border-zinc-200 bg-white p-4">
            <input className="w-full text-sm font-medium px-2 py-1.5 border border-zinc-200 outline-none focus:border-black" value={b.name} onChange={setB(i, "name")} data-testid={`g06-name-${i}`} />
            <div className="mt-2 grid grid-cols-3 gap-2">
              {CRITERIA.map(([k, label]) => (
                <label key={k} className="text-[11px] text-zinc-500">
                  {label}
                  <input type="number" min={1} max={5} className="mt-0.5 w-full px-2 py-1 text-sm border border-zinc-200 outline-none focus:border-black" value={b[k]} onChange={setB(i, k)} data-testid={`g06-${i}-${k}`} />
                </label>
              ))}
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setBets((bs) => [...bs, { name: "", impact: 3, speed: 3, fit: 3, investment: 3, risk: 3, confidence: 3 }])} className="text-sm font-medium underline underline-offset-4" data-testid="g06-add">+ Add bet</button>
        <ToolSection title="Weights (0–10)" testid="g06-weights">
          <div className="grid grid-cols-3 gap-2">
            {CRITERIA.map(([k, label]) => <Field key={k} label={label} value={w[k]} onChange={setW(k)} testid={`g06-w-${k}`} />)}
          </div>
        </ToolSection>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Ranked portfolio</div>
        <div className="mt-3 space-y-2" data-testid="g06-result">
          {r.map((b) => (
            <div key={b.name} className="flex items-center justify-between border border-black/10 px-4 py-3">
              <div>
                <span className="overline mr-2">#{b.rank}</span>
                <span className="text-sm font-medium">{b.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm tabular-nums">{b.score}/100</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 ${b.bucket === "now" ? "bg-black text-white" : b.bucket === "next" ? "bg-zinc-200" : "bg-zinc-100 text-zinc-500"}`}>{b.bucket}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4"><Note>Now = fund and staff this quarter. Next = validate the weakest assumption first. Later = revisit next cycle. No-go = stop spending meeting time on it.</Note></div>
      </div>
    </div>
  );
}

/* ---------- G05 Growth Pools & White-Space Mapper ---------- */
export function ToolG05() {
  const [pools, setPools] = useState([
    { name: "Tier-2 GT expansion", size: "unknown", attractiveness: 4, win: 3, evidence: "Two inbound distributor enquiries" },
    { name: "Premium tier", size: "est. ₹8 Cr", attractiveness: 3, win: 4, evidence: "Competitor B selling at 2× price with weak service" },
  ]);
  const setP = (i, k) => (e) => setPools((ps) => ps.map((p, j) => (j === i ? { ...p, [k]: e.target.value } : p)));
  return (
    <div>
      <p className="text-sm text-zinc-600 max-w-2xl">Define each candidate growth space with its demand evidence. Unknown size stays "unknown" — a map built on invented TAMs sends you to the wrong geography.</p>
      <div className="mt-5 space-y-3">
        {pools.map((p, i) => (
          <div key={i} className="border border-zinc-200 bg-white p-4 grid md:grid-cols-[1fr_110px_110px_110px_1fr] gap-2 items-center">
            <input className="text-sm px-2 py-1.5 border border-zinc-200 outline-none focus:border-black" placeholder="Pool name" value={p.name} onChange={setP(i, "name")} data-testid={`g05-name-${i}`} />
            <input className="text-sm px-2 py-1.5 border border-zinc-200 outline-none focus:border-black" placeholder="Size (or unknown)" value={p.size} onChange={setP(i, "size")} data-testid={`g05-size-${i}`} />
            <label className="text-[11px] text-zinc-500">Attract. 1–5<input type="number" min={1} max={5} className="mt-0.5 w-full px-2 py-1 text-sm border border-zinc-200" value={p.attractiveness} onChange={setP(i, "attractiveness")} data-testid={`g05-att-${i}`} /></label>
            <label className="text-[11px] text-zinc-500">Right to win<input type="number" min={1} max={5} className="mt-0.5 w-full px-2 py-1 text-sm border border-zinc-200" value={p.win} onChange={setP(i, "win")} data-testid={`g05-win-${i}`} /></label>
            <input className="text-sm px-2 py-1.5 border border-zinc-200 outline-none focus:border-black" placeholder="Evidence / source" value={p.evidence} onChange={setP(i, "evidence")} data-testid={`g05-ev-${i}`} />
          </div>
        ))}
        <button type="button" onClick={() => setPools((ps) => [...ps, { name: "", size: "unknown", attractiveness: 3, win: 3, evidence: "" }])} className="text-sm font-medium underline underline-offset-4" data-testid="g05-add">+ Add pool</button>
      </div>
      <ToolSection title="Attractiveness × right-to-win map" testid="g05-map">
        <div className="border border-zinc-200 bg-white p-4">
          <div className="relative h-72 border-l-2 border-b-2 border-black">
            <span className="absolute -left-1 top-1 text-[10px] text-zinc-400 [writing-mode:vertical-lr] rotate-180">Attractiveness →</span>
            <span className="absolute bottom-1 right-2 text-[10px] text-zinc-400">Right to win →</span>
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-zinc-200" />
            <div className="absolute top-1/2 left-0 right-0 h-px bg-zinc-200" />
            {pools.filter((p) => p.name).map((p, i) => (
              <div
                key={i}
                className="absolute -translate-x-1/2 -translate-y-1/2 bg-black text-white text-[11px] px-2 py-1 whitespace-nowrap"
                style={{ left: `${(p.win / 5) * 92 + 4}%`, bottom: `${(p.attractiveness / 5) * 92 + 2}%` }}
                title={`${p.name} — size: ${p.size}`}
                data-testid={`g05-dot-${i}`}
              >
                {p.name}{p.size === "unknown" ? " (?)" : ""}
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-zinc-500">Top-right = pursue. Top-left = build the evidence or the capability before committing. "(?)" marks pools with unknown size — research them before sizing the bet.</p>
        </div>
      </ToolSection>
    </div>
  );
}

/* ---------- G07 Annual Growth Strategy on One Page ---------- */
export function ToolG07() {
  const [v, set] = useNumbers({
    baseline: "₹12 Cr revenue, 18% contribution", ambition: "₹18 Cr with ≥20% contribution",
    segments: "", proposition: "", drivers: "", exclusions: "", kpis: "", owners: "",
  });
  const rows = [
    ["Where we are (baseline)", "baseline"], ["Where we're going (ambition)", "ambition"],
    ["Who we win with (target segments)", "segments"], ["Why they choose us (proposition)", "proposition"],
    ["The 3–5 growth drivers", "drivers"], ["What we will NOT do (stop-doing)", "exclusions"],
    ["How we'll measure it (KPIs)", "kpis"], ["Who owns what", "owners"],
  ];
  const gaps = rows.filter(([, k]) => !String(v[k]).trim()).map(([l]) => l);
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <p className="text-sm text-zinc-600">Fill the canvas. The one-page summary updates live — print it for the leadership meeting.</p>
        {rows.map(([label, k]) => (
          <label key={k} className="block">
            <span className="text-sm font-medium text-zinc-800">{label}</span>
            <textarea rows={2} className="mt-1.5 w-full px-3 py-2 text-sm border border-zinc-300 outline-none focus:border-black bg-white" value={v[k]} onChange={(e) => set(k)(e.target.value)} data-testid={`g07-${k}`} />
          </label>
        ))}
      </div>
      <div>
        <div className="border-2 border-black bg-white p-6 md:p-8" data-testid="g07-canvas">
          <div className="overline">Annual Growth Strategy — One Page</div>
          <div className="mt-4 space-y-3">
            {rows.map(([label, k]) => (
              <div key={k} className="border-b border-black/10 pb-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{label}</div>
                <p className={`mt-1 text-sm leading-relaxed ${String(v[k]).trim() ? "text-zinc-800" : "text-zinc-300 italic"}`}>
                  {String(v[k]).trim() || "Not decided yet."}
                </p>
              </div>
            ))}
          </div>
        </div>
        {gaps.length > 0 && (
          <div className="mt-4"><Note tone="warn">Undecided: {gaps.join(", ")}. A target without drivers is a wish; a driver without an owner is a hope.</Note></div>
        )}
      </div>
    </div>
  );
}

/* ---------- G08 90-Day Growth Sprint Builder ---------- */
const SPRINT_LIBRARY = {
  "sales-conversion": { label: "Sales conversion leak", weeks: ["Baseline funnel stages & conversion rates; fix stage definitions", "Win/loss calls on last 10 lost deals; rewrite follow-up sequence", "Proposal teardown and rewrite of 3 live proposals", "Rep coaching on discovery; daily pipeline hygiene checks", "Objection-handling playbook; stage-exit criteria enforced", "Pricing/follow-up exceptions reviewed with sales head", "A/B two proposal formats on live deals", "Conversion experiment readout; kill or scale", "Implement winning follow-up SLA in CRM", "Territory-level conversion review; address outliers", "Document the new process; train all reps", "Full-funnel metrics vs baseline", "Retrospective; decide next 90-day constraint"] },
  "pricing-leakage": { label: "Pricing & discount leakage", weeks: ["Pull discount data by rep/channel/SKU; build pocket-price view", "Identify top 10 leak sources; agree guardrails", "Draft discount authority matrix; leadership sign-off", "Communicate guardrails to sales; objection prep", "Guardrails live; weekly exception log starts", "Reprice 3 worst SKUs/deals as pilots", "Pilot results review; adjust floors", "Trade-scheme audit; stop unreconciled schemes", "Customer communication plan for price corrections", "Monthly pocket-price dashboard live", "Guardrail compliance review", "Margin bridge vs baseline", "Retrospective; next constraint"] },
  "distribution-coverage": { label: "Distribution coverage gap", weeks: ["Map outlet universe vs billed outlets; numeric distribution baseline", "Distributor scorecards for top 10 partners", "Identify white-space territories; beat-plan draft", "Distributor ROI conversations; fix partner economics", "New beat plans live in 2 pilot territories", "Range-selling drive: must-stock SKU list", "Pilot territory readout; coverage vs target", "Fix supply/service issues blocking coverage", "Scale beat plans to next territories", "Distributor joint business plans signed", "Coverage dashboard weekly review", "Full readout vs baseline", "Retrospective; next constraint"] },
  "demand-generation": { label: "Weak demand generation", weeks: ["Define ICP and the 5 buyer questions that matter", "Audit current spend vs qualified demand produced", "Rewrite one landing page around buyer problem", "Launch 2 high-intent campaigns; pause worst spend", "Lead-quality feedback loop with sales starts", "Creative test: 3 messages × 2 audiences", "First readout; kill bottom half", "Double down on working message; add proof assets", "Sales-marketing SLA signed", "Scale budget on proven cells only", "Pipeline attribution review", "Cost per qualified opportunity vs baseline", "Retrospective; next constraint"] },
};
const SPRINT_PHASES = ["Diagnose", "Diagnose", "Execute", "Execute", "Execute", "Execute", "Optimise", "Optimise", "Optimise", "Optimise", "Scale", "Scale", "Review"];

export function ToolG08() {
  const [constraint, setConstraint] = useState("sales-conversion");
  const [tasks, setTasks] = useState(null);
  const plan = (tasks || SPRINT_LIBRARY[constraint].weeks).map((t, i) => ({ week: i + 1, phase: SPRINT_PHASES[i], task: t }));
  return (
    <div>
      <div className="flex flex-wrap gap-2" data-testid="g08-constraints">
        {Object.entries(SPRINT_LIBRARY).map(([k, v]) => (
          <button key={k} type="button" onClick={() => { setConstraint(k); setTasks(null); }}
            className={`h-10 px-4 text-sm font-medium border transition-colors ${constraint === k ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
            data-testid={`g08-${k}`}>
            {v.label}
          </button>
        ))}
      </div>
      <div className="mt-6 space-y-2" data-testid="g08-plan">
        {plan.map((w) => (
          <div key={w.week} className="grid grid-cols-[70px_90px_1fr] gap-3 items-center border border-zinc-200 bg-white px-3 py-2">
            <span className="overline">Week {w.week}</span>
            <span className={`text-[10px] font-semibold uppercase tracking-wider ${w.phase === "Diagnose" ? "text-amber-700" : w.phase === "Review" ? "text-emerald-700" : "text-zinc-500"}`}>{w.phase}</span>
            <input className="text-sm px-2 py-1.5 border border-transparent hover:border-zinc-200 focus:border-black outline-none bg-transparent" value={w.task}
              onChange={(e) => setTasks(plan.map((p) => (p.week === w.week ? e.target.value : p.task)))} data-testid={`g08-week-${w.week}`} />
          </div>
        ))}
      </div>
      <div className="mt-4"><Note>Every task is editable — assign owners and dates before week 1. The plan follows diagnose → execute → optimise → scale deliberately: skipping diagnosis is how sprints become busywork.</Note></div>
    </div>
  );
}

/* ---------- G09 Value Creation & KPI Driver Tree ---------- */
export function ToolG09() {
  const [v, set] = useNumbers({ customers: 1200, ordersPerCustomer: 3, aov: 5000, contributionPct: 28 });
  const r = useMemo(() => {
    const revenue = (Number(v.customers) || 0) * (Number(v.ordersPerCustomer) || 0) * (Number(v.aov) || 0);
    const contribution = revenue * (Number(v.contributionPct) || 0) / 100;
    return { revenue, contribution };
  }, [v]);
  const Node = ({ label, k, prefix = "", suffix = "" }) => (
    <div className="border border-zinc-300 bg-white p-3 text-center">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 flex items-center justify-center">
        {prefix && <span className="text-xs text-zinc-400 mr-1">{prefix}</span>}
        <input type="number" className="w-24 text-center text-sm font-medium border-b border-zinc-300 outline-none focus:border-black" value={v[k]} onChange={(e) => set(k)(e.target.value)} data-testid={`g09-${k}`} />
        {suffix && <span className="text-xs text-zinc-400 ml-1">{suffix}</span>}
      </div>
    </div>
  );
  return (
    <div>
      <p className="text-sm text-zinc-600 max-w-2xl">Trace the number that matters back to the levers someone can pull. Edit any driver; the tree recomputes.</p>
      <div className="mt-6 border border-zinc-200 bg-[#fafafa] p-6 overflow-x-auto" data-testid="g09-tree">
        <div className="min-w-[640px]">
          <div className="flex justify-center">
            <div className="border-2 border-black bg-white px-8 py-4 text-center">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Contribution</div>
              <div className="font-serif-display text-3xl mt-1" data-testid="g09-contribution">{inr(r.contribution)}</div>
            </div>
          </div>
          <div className="flex justify-center my-2 text-zinc-300">↑ ×</div>
          <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
            <div className="border-2 border-zinc-400 bg-white px-4 py-3 text-center">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Revenue</div>
              <div className="font-serif-display text-2xl" data-testid="g09-revenue">{inr(r.revenue)}</div>
            </div>
            <Node label="Contribution margin" k="contributionPct" suffix="%" />
          </div>
          <div className="flex justify-center my-2 text-zinc-300">↑ = × ×</div>
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            <Node label="Active customers" k="customers" />
            <Node label="Orders / customer" k="ordersPerCustomer" />
            <Node label="Avg order value" k="aov" prefix="₹" />
          </div>
        </div>
      </div>
      <div className="mt-4"><Note>Revenue drivers multiply — a 10% lift in customers AND order value is +21% revenue, not +20%. Contribution is not EBITDA: overheads sit below this line and need their own owners.</Note></div>
    </div>
  );
}

/* ---------- G10 Founder-Led Growth Transition ---------- */
const G10_AREAS = ["Finding leads", "First meetings", "Pricing decisions", "Closing big deals", "Key account relationships", "Hiring & team"];

export function ToolG10() {
  const [dep, setDep] = useState(Object.fromEntries(G10_AREAS.map((a) => [a, 3])));
  const [ready, setReady] = useState(Object.fromEntries(G10_AREAS.map((a) => [a, 1])));
  const overall = Math.round((G10_AREAS.reduce((s, a) => s + dep[a], 0) / (G10_AREAS.length * 5)) * 100);
  const phases = G10_AREAS.map((a, i) => {
    const gap = dep[a] - ready[a];
    return { area: a, dep: dep[a], ready: ready[a], gap, phase: gap >= 3 ? "Document + shadow (weeks 1–4)" : gap >= 2 ? "Co-own with founder review (weeks 3–8)" : "Delegate with monthly review (now)" };
  }).sort((a, b) => b.gap - a.gap);
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <p className="text-sm text-zinc-600">Score each area: how much does it depend on you (1–5), and how ready is the next person to own it (1–5)?</p>
        {G10_AREAS.map((a) => (
          <div key={a} className="border border-zinc-200 bg-white p-4">
            <div className="text-sm font-medium">{a}</div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <label className="text-[11px] text-zinc-500">Founder dependency
                <input type="range" min={1} max={5} value={dep[a]} onChange={(e) => setDep((d) => ({ ...d, [a]: Number(e.target.value) }))} className="w-full accent-black" data-testid={`g10-dep-${a.replace(/\W+/g, "-")}`} />
                <span className="tabular-nums">{dep[a]}/5</span>
              </label>
              <label className="text-[11px] text-zinc-500">Next-leader readiness
                <input type="range" min={1} max={5} value={ready[a]} onChange={(e) => setReady((d) => ({ ...d, [a]: Number(e.target.value) }))} className="w-full accent-black" data-testid={`g10-ready-${a.replace(/\W+/g, "-")}`} />
                <span className="tabular-nums">{ready[a]}/5</span>
              </label>
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="border border-zinc-200 bg-white p-6">
          <div className="overline">Founder dependency index</div>
          <div className="font-serif-display text-5xl mt-2" data-testid="g10-index">{overall}<span className="text-2xl text-zinc-400">%</span></div>
          <p className="mt-2 text-sm text-zinc-600">{overall >= 70 ? "The business sells through you. Every hire is a key-person risk until this drops." : overall >= 40 ? "Partial dependency — the transition plan below sequences the handover." : "Healthy distribution of selling responsibility."}</p>
        </div>
        <ToolSection title="Transition sequence (highest gap first)" testid="g10-plan">
          <div className="space-y-2">
            {phases.map((p) => (
              <div key={p.area} className="flex items-center justify-between gap-4 border border-black/10 px-4 py-3">
                <div>
                  <div className="text-sm font-medium">{p.area}</div>
                  <div className="text-xs text-zinc-500">{p.phase}</div>
                </div>
                <span className="text-xs tabular-nums text-zinc-400">gap {p.gap}</span>
              </div>
            ))}
          </div>
        </ToolSection>
        <div className="mt-4"><Note>Transfer relationship capital deliberately: joint meetings before solo ones, and introduce the new owner as an upgrade ("you now get a dedicated specialist"), not a downgrade.</Note></div>
      </div>
    </div>
  );
}
